import { useState, useEffect } from 'react';
import { Card, Empty, Spin, message, Alert, Modal, Input } from 'antd';
import { Button } from '@bancobarigui/barihaus';
import {
  FileTextOutlined,
  EyeOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ToolOutlined,
  SendOutlined,
  CalculatorOutlined,
  RollbackOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { InspectionRequest } from '@/services/types/inspection.types';
import { videoSessionService } from '@/services/videoSession.service';
import { LaudoWizard } from './LaudoWizard';
import { ViewReportModal } from './ViewReportModal';
import { ReviewReportModal } from './ReviewReportModal';
import { AssignInspectorModal } from '@/components/AssignInspectorModal';
import { InspectionReport, inspectionReportService, pickEditableReport } from '@/services/inspectionReport.service';
import { inspectionReportReviewService } from '@/services/inspectionReportReview.service';
import { LaudoWizardData } from '../types/laudo-wizard.types';
import { InspectionItemStatus } from '@/shared/enums/InspectionStatus';
import { VideoSessionStatus } from '@/shared/enums/VideoSessionStatus';
import { InspectionReportReview } from '@/shared/types/inspectionReportReview.types';

import {
  LaudoTabHeader,
  PendingCorrectionAlert,
  ProviderCorrectionCard,
  ReportCard,
  EmptyReportState,
  AutomaticLaudoSection,
} from './LaudoTabComponents';
import { AssignCalculationsModal } from './AssignCalculationsModal';
import { inspectionAssignmentService } from '@/services/inspectionAssignment.service';
import aiAnalysisService from '@/services/aiAnalysis.service';
import { ReturnAfterApprovalModal } from './ReturnAfterApprovalModal';
import { useLaudoPermissions } from '../hooks/useLaudoPermissions';
import { useAutomaticLaudo } from '../hooks/useAutomaticLaudo';
import { useLaudoReports } from '../hooks/useLaudoReports';
import { usePreliminaryCalculations } from '../hooks/usePreliminaryCalculations';

import { normalizeStatus } from '../utils/statusHelpers';

interface LaudoTabProps {
  inspection: InspectionRequest;
  onRefresh?: () => void;
}

export function LaudoTab({ inspection, onRefresh }: LaudoTabProps) {

  const [showWizard, setShowWizard] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [editingReport, setEditingReport] = useState<{
    report: InspectionReport;
    data: LaudoWizardData;
    isAutomatic?: boolean;
  } | null>(null);
  const [correctingReport, setCorrectingReport] = useState<{
    report: InspectionReport;
    data: LaudoWizardData;
    review: InspectionReportReview | null;
    isExternalReturn?: boolean;
    externalReturnData?: {
      reason: string;
      description: string | null;
    };
  } | null>(null);
  const [calculationsReport, setCalculationsReport] = useState<{
    report: InspectionReport;
    data: LaudoWizardData;
  } | null>(null);
  const [forceManualAutomatic, setForceManualAutomatic] = useState(false);
  const [showAssignCalculationsModal, setShowAssignCalculationsModal] = useState(false);
  const [showForwardAutomaticModal, setShowForwardAutomaticModal] = useState(false);
  const [showReturnAfterApprovalModal, setShowReturnAfterApprovalModal] = useState(false);
  const [showAssignInspectorModal, setShowAssignInspectorModal] = useState(false);
  const [showReturnToInspectorModal, setShowReturnToInspectorModal] = useState(false);
  const [returnToInspectorNotes, setReturnToInspectorNotes] = useState('');
  const handleRedirectToInspector = async (notes?: string) => {
    const assignments = inspection.items?.[0]?.assignments ?? [];
    const inspectorAssignment = [...assignments]
      .reverse()
      .find((a) => !!a.inspectorId);

    if (!inspectorAssignment) {
      message.error('Não há inspetor original atribuído para devolução.');
      return;
    }

    try {
      message.loading({ content: 'Devolvendo ao inspetor...', key: 'redirect-inspector' });
      await inspectionAssignmentService.reassignInspector(inspectorAssignment.id, {
        notes: notes || (externalReturnData
          ? `Devolução após negativa do banco. Motivo: ${externalReturnData.reason}`
          : 'Devolução ao inspetor para correção do checklist'),
      });
      message.success({ content: 'Inspeção devolvida ao inspetor com sucesso!', key: 'redirect-inspector' });
      setLocalStatusOverride(InspectionItemStatus.DevolvidoPelaPrestadora);
      onRefresh?.();
    } catch {
      message.error({ content: 'Erro ao devolver inspeção ao inspetor', key: 'redirect-inspector' });
    }
  };
  const [hasCompletedVideoCall, setHasCompletedVideoCall] = useState(false);
  const [isLoadingVideoCheck, setIsLoadingVideoCheck] = useState(true);
  const [localStatusOverride, setLocalStatusOverride] = useState<InspectionItemStatus | null>(null);
  const [externalReturnData, setExternalReturnData] = useState<{
    reason: string;
    description: string | null;
  } | null>(null);


  const rawItemStatus = localStatusOverride ?? inspection.items?.[0]?.status;
  const itemStatus = normalizeStatus(rawItemStatus);
  const itemId = inspection.items?.[0]?.id;
  const activeAssignment = inspection.items?.[0]?.assignments?.slice(-1)[0];


  const permissions = useLaudoPermissions(itemStatus);
  const {
    isInspector,
    isProvider,
    isBank,
    reviewerType,
    isAutomaticReportFlow,
    isAwaitingBankFill,
    isAwaitingProviderReview,
    isRejectedByBank,
    isAwaitingCalculations,
    isDevolvidoPelaPrestadora,
    inspectorCanCreateOrEdit,
    providerCanCorrect,
  } = permissions;

  const automaticLaudo = useAutomaticLaudo({
    itemId,
    itemStatus,
    isAutomaticReport: inspection.items?.[0]?.isAutomaticReport,
    onRefresh: onRefresh ?? (() => {}),
  });
  const {
    isInAutomaticFlow,
    handleAutomaticReportReady,
    handleAutomaticReportRetry,
  } = automaticLaudo;

  const reportsState = useLaudoReports({
    itemId,
    currentItemStatus: itemStatus,
    isProvider,
  });
  const {
    reports,
    loading,
    pendingReview,
    reviewHistory,
    selectedReport,
    setSelectedReport,
    loadReports,
    parseReportWizardData,
  } = reportsState;

  const preliminaryCalc = usePreliminaryCalculations(inspection);


  const canCreateLaudo =
    !isDevolvidoPelaPrestadora && (
      itemStatus === InspectionItemStatus.AguardandoRevisaoPrestadora ||
      itemStatus === InspectionItemStatus.TelevistoriaFinalizada ||
      itemStatus === InspectionItemStatus.AguardandoPreenchimentoPrestadora ||
      itemStatus === InspectionItemStatus.AguardandoPreenchimentoBanco ||
      hasCompletedVideoCall
    );

  const isAwaitingBankReview = itemStatus === InspectionItemStatus.AguardandoRevisaoBanco;
  const isBankInspectorCorrecting = itemStatus === InspectionItemStatus.CorrecaoPeloInspetorBanco;
  const isApprovedByBank = itemStatus === InspectionItemStatus.LaudoAceitoPeloBanco;
  const isReturnedByExternalSystem = itemStatus === InspectionItemStatus.LaudoDevolvido; // Status 21
  const isDevolvidoAposAceite = itemStatus === InspectionItemStatus.DevolvidoPeloBancoAposAceite; // Status 30

  // Banco pode corrigir quando:
  // - Status 21 (laudo devolvido por sistema externo, banco pode corrigir diretamente ou encaminhar para prestadora)
  const bankCanCorrect = isReturnedByExternalSystem;

  // Está em revisão (somente visualização para inspetor)
  // NOTA: Status 17 NÃO está incluído porque o inspetor precisa poder corrigir nesse status
  const isUnderReview = isAwaitingProviderReview || isAwaitingBankReview || isApprovedByBank;

  const getStatusMessage = (): {
    type: 'info' | 'success' | 'warning' | 'error';
    message: string;
    description: string;
  } | null => {
    if (isAwaitingBankFill && reports.length === 0) {
      if (isBank) {
        return {
          type: 'warning',
          message: 'Laudo Automático (AVM) - Ação Necessária',
          description: 'Este tipo de inspeção é preenchido pelo próprio Banco, sem prestadora. Preencha o laudo de avaliação — ao enviar, ele já fica como Laudo Aceito pelo Banco.',
        };
      }
      return {
        type: 'info',
        message: 'Aguardando Preenchimento pelo Banco',
        description: 'O Banco está preenchendo o laudo de avaliação (AVM, sem prestadora).',
      };
    }

    if (isAutomaticReportFlow && reports.length === 0) {
      if (isProvider) {
        return {
          type: 'warning',
          message: 'Laudo Automático - Ação Necessária',
          description: 'Este tipo de inspeção não requer inspetor. Preencha o laudo de avaliação e envie diretamente para o Banco.',
        };
      }
      if (isBank) {
        return {
          type: 'info',
          message: 'Aguardando Laudo da Prestadora',
          description: 'A Prestadora está preenchendo o laudo de avaliação (sem inspetor).',
        };
      }
    }

    if (isAwaitingProviderReview && reports.length === 0) {
      if (isInspector) {
        return {
          type: 'info',
          message: 'Aguardando Revisão da Prestadora',
          description: 'O checklist foi preenchido e enviado. Aguardando a Prestadora gerar o laudo.',
        };
      }
      if (isProvider) {
        return {
          type: 'warning',
          message: 'Ação Necessária: Gerar Laudo',
          description: 'O Inspetor completou o checklist. Gere o laudo de avaliação.',
        };
      }
    }

    if (isAwaitingBankReview) {
      if (isInspector || isProvider) {
        return {
          type: 'info',
          message: 'Aguardando Revisão do Banco',
          description: 'O laudo foi gerado pela Prestadora e está aguardando a revisão do Banco.',
        };
      }
      if (isBank) {
        return {
          type: 'warning',
          message: 'Ação Necessária: Revisar Laudo',
          description:
            'O laudo foi gerado pela Prestadora e está aguardando sua revisão. Aprove ou aponte correções necessárias.',
        };
      }
    }

    if (isRejectedByBank) {
      if (isInspector) {
        return {
          type: 'info',
          message: 'Laudo Rejeitado pelo Banco',
          description: 'O Banco rejeitou o laudo. A Prestadora está fazendo as correções.',
        };
      }
      if (isProvider) {
        // Se há dados de devolução externa, mostra eles na descrição
        if (externalReturnData) {
          return {
            type: 'warning',
            message: 'Ação Necessária: Corrigir Laudo Devolvido pelo Sistema Externo',
            description: `Motivo: ${externalReturnData.reason}${externalReturnData.description ? `\n${externalReturnData.description}` : ''}`,
          };
        }
        return {
          type: 'warning',
          message: 'Ação Necessária: Corrigir Laudo',
          description:
            'O Banco apontou correções no laudo. Faça as correções e reenvie.',
        };
      }
      if (isBank) {
        return {
          type: 'info',
          message: 'Laudo Rejeitado',
          description: 'Você rejeitou o laudo. Aguardando correções da Prestadora.',
        };
      }
    }

    if (isApprovedByBank) {
      return {
        type: 'success',
        message: 'Laudo Aprovado',
        description: isBank
          ? 'O laudo foi aprovado. Caso necessário, você pode editá-lo e retransmitir, ou devolver para a prestadora solicitar correções.'
          : 'O laudo foi aprovado pelo banco. Processo concluído com sucesso!',
      };
    }

    if (isDevolvidoAposAceite) {
      if (isBank) {
        return {
          type: 'warning',
          message: 'Laudo Devolvido para Correção',
          description: 'Você devolveu o laudo para a prestadora após o aceite. Aguardando correções.',
        };
      }
      if (isProvider) {
        return {
          type: 'error',
          message: 'Ação Necessária: Laudo Devolvido pelo Banco',
          description: externalReturnData
            ? `Motivo: ${externalReturnData.reason}${externalReturnData.description ? `\n${externalReturnData.description}` : ''}`
            : 'O banco devolveu o laudo após o aceite para correções. Corrija e reenvie o laudo.',
        };
      }
      if (isInspector) {
        return {
          type: 'info',
          message: 'Laudo Devolvido pelo Banco',
          description: 'O banco devolveu o laudo após o aceite. Aguardando ação da prestadora.',
        };
      }
    }

    // Status 29 - Aguardando cálculos de prestadora
    if (isAwaitingCalculations) {
      if (isBank) {
        return {
          type: 'info',
          message: 'Aguardando Cálculos da Prestadora',
          description: 'Os cálculos foram encaminhados para outra prestadora. Aguardando o preenchimento.',
        };
      }
      if (isProvider) {
        return {
          type: 'warning',
          message: 'Ação Necessária: Preencher Cálculos',
          description: 'O banco encaminhou os cálculos deste laudo para você. Preencha a metodologia e transmita.',
        };
      }
      if (isInspector) {
        return {
          type: 'info',
          message: 'Aguardando Cálculos',
          description: 'O laudo está aguardando o preenchimento dos cálculos por outra prestadora.',
        };
      }
    }

    // 🔄 Status 21 - Laudo devolvido por sistema externo
    if (isReturnedByExternalSystem) {
      if (isBank) {
        return {
          type: 'warning',
          message: 'Ação Necessária: Laudo Devolvido pelo Sistema Externo',
          description:
            'O laudo foi devolvido pelo sistema externo. Você pode corrigir diretamente ou encaminhar para a prestadora.',
        };
      }
      if (isProvider || isInspector) {
        return {
          type: 'info',
          message: 'Laudo Devolvido pelo Sistema Externo',
          description: 'O laudo foi devolvido pelo sistema externo. Aguardando ação do banco.',
        };
      }
    }

    return null;
  };

  useEffect(() => {
    const checkCompletedVideoCall = async () => {
      setIsLoadingVideoCheck(true);

      if (!inspection?.items || inspection.items.length === 0) {
        setHasCompletedVideoCall(false);
        setIsLoadingVideoCheck(false);
        return;
      }

      try {
        for (const item of inspection.items) {
          if (!item.id) continue;

          try {
            const sessions = await videoSessionService.getVideoSessionsByInspectionItemId(item.id);

            if (sessions && Array.isArray(sessions)) {
              const hasCompleted = sessions.some(
                (session) => session.status === VideoSessionStatus.Completed && session.actualEndTime
              );

              if (hasCompleted) {
                setHasCompletedVideoCall(true);
                setIsLoadingVideoCheck(false);
                return;
              }
            }
          } catch {
            // Error checking video sessions
          }
        }
        setHasCompletedVideoCall(false);
      } catch {
        setHasCompletedVideoCall(false);
      }
      setIsLoadingVideoCheck(false);
    };

    if (inspection) {
      checkCompletedVideoCall();
    }
  }, [inspection]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // Buscar dados da devolução sempre que houver itemId.
  // Necessário também em status posteriores (AguardandoRevisaoPrestadora pós-devolução),
  // para diferenciar o ciclo "pós-devolução do banco/SF" do ciclo inicial.
  useEffect(() => {
    const fetchExternalReturnData = async () => {
      if (!itemId) {
        setExternalReturnData(null);
        return;
      }
      try {
        const returnData = await inspectionReportReviewService.getReportReturnByInspectionItemId(itemId);
        if (returnData) {
          setExternalReturnData({
            reason: returnData.reason,
            description: returnData.description,
          });
        } else {
          setExternalReturnData(null);
        }
      } catch {
        setExternalReturnData(null);
      }
    };

    fetchExternalReturnData();
  }, [itemId, itemStatus]);


  const handleViewReport = (report: InspectionReport) => {
    setSelectedReport(report);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedReport(null);
  };

  const handleReviewReport = (report: InspectionReport) => {
    setSelectedReport(report);
    setShowReviewModal(true);
  };

  const handleCloseReviewModal = () => {
    setShowReviewModal(false);
    setSelectedReport(null);
  };

  const handleReviewed = (action: 'approved' | 'rejected' | 'edited' | 'forwarded') => {
    if (action === 'forwarded') {
      onRefresh?.();
      return;
    }

    if (action === 'approved') {
      if (isProvider) {
        setLocalStatusOverride(InspectionItemStatus.AguardandoRevisaoBanco);
      } else if (isBank) {
        setLocalStatusOverride(InspectionItemStatus.LaudoAceitoPeloBanco);
      }
    } else if (action === 'rejected') {
      if (isProvider) {
        setLocalStatusOverride(InspectionItemStatus.LaudoNegadoPorPrestadora);
      } else if (isBank) {
        setLocalStatusOverride(InspectionItemStatus.LaudoNegadoPorBanco);
      }
    } else if (action === 'edited') {
      setLocalStatusOverride(InspectionItemStatus.AguardandoRevisaoBanco);
    }

    loadReports();
    onRefresh?.();
  };

  const handleEditReport = async (report: InspectionReport) => {
    if (!report.reportData) {
      message.error('Este laudo não possui dados para edição');
      return;
    }

    try {
      const reportDataParsed = JSON.parse(report.reportData);
      const wizardData = reportDataParsed.wizardData;

      if (!wizardData) {
        message.error('Dados do wizard não encontrados');
        return;
      }

      const isAutomaticReport = inspection.items?.[0]?.isAutomaticReport === true
        || reportDataParsed.isAutomaticReport === true;

      const cleanedWizardData = {
        ...wizardData,
        vistoria: {
          ...wizardData.vistoria,
          checklistRespostas: {},
          processedResponses: undefined,
          fotoFachada: isAutomaticReport ? wizardData.vistoria?.fotoFachada : undefined,
        },
        signature: {
          ...wizardData.signature,
          signatureImage: '',
        },
      };

      setEditingReport({ report, data: cleanedWizardData, isAutomatic: isAutomaticReport });
      setShowWizard(true);
    } catch {
      message.error('Erro ao carregar dados do laudo para edição');
    }
  };

  const handleCloseWizard = async (wasTransmitted?: boolean) => {
    const wasCalculationsMode = !!calculationsReport;

    if (wasTransmitted) {
      setLocalStatusOverride(null);
    }

    setShowWizard(false);
    setEditingReport(null);
    setCorrectingReport(null);
    setCalculationsReport(null);
    setForceManualAutomatic(false);

    if (wasTransmitted) {
      if (wasCalculationsMode) {
        preliminaryCalc.clearDraft();
      }

      // Se um laudo foi transmitido, aguarda um pouco para o backend processar
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // Força refresh da inspeção PRIMEIRO para atualizar o status
    if (onRefresh) {
      onRefresh();
    }

    // Aguarda um pouco para o onRefresh completar
    await new Promise(resolve => setTimeout(resolve, 500));

    // Recarrega os laudos
    await loadReports();
  };

  const handleCorrectReport = async (report: InspectionReport) => {
    if (!report.reportData) {
      message.error('Este laudo não possui dados para correção');
      return;
    }

    // Se é devolução externa (status 21) ou devolução pós-aceite (status 30), não precisa de pendingReview
    const isExternalReturn = isReturnedByExternalSystem || isDevolvidoAposAceite;

    if (!pendingReview && !isExternalReturn) {
      message.error('Não há correções pendentes para este laudo');
      return;
    }

    const wizardData = parseReportWizardData(report);

    if (!wizardData) {
      message.error('Dados do wizard não encontrados');
      return;
    }

      // Buscar dados de devolução externa se for o caso
      let externalReturnData: { reason: string; description: string | null } | undefined = undefined;
      if (isExternalReturn) {
        const itemId = inspection.items?.[0]?.id;
        if (itemId) {
          try {
            const returnData = await inspectionReportReviewService.getReportReturnByInspectionItemId(itemId);
            if (returnData) {
              externalReturnData = {
                reason: returnData.reason,
                description: returnData.description,
              };
            }
          } catch {
            // Continua mesmo sem os dados - não é crítico
          }
        }
      }

      // Se é devolução externa, passa null como review para indicar que todos os campos são editáveis
      setCorrectingReport({
        report,
        data: wizardData,
        review: isExternalReturn ? null : pendingReview!,
        isExternalReturn, // Flag para indicar devolução externa
        externalReturnData, // Dados de devolução (motivo e descrição)
      });
      setShowWizard(true);
  };

  const handleProviderCorrectMyself = async (report?: InspectionReport) => {
    const targetReport = report || selectedReport;
    // Valida que tem o report e pelo menos um dos dois: pendingReview, externalReturnData ou devolução pós-aceite
    if (!targetReport || (!pendingReview && !externalReturnData && !isDevolvidoAposAceite)) {
      message.error('Dados do laudo não encontrados');
      return;
    }

    const wizardData = parseReportWizardData(targetReport);

      if (!wizardData) {
        message.error('Dados do wizard não encontrados');
        return;
      }

      // Se é devolução pós-aceite ou devolução externa, trata como external return (todos os campos editáveis)
      const isReturnFlow = !!externalReturnData || isDevolvidoAposAceite;
      setCorrectingReport({
        report: targetReport,
        data: wizardData,
        review: isReturnFlow ? null : pendingReview,
        isExternalReturn: isReturnFlow,
        externalReturnData: externalReturnData || undefined,
      });
      setShowWizard(true);
  };

  const handleForwardToProvider = () => {
    const itemId = inspection.items?.[0]?.id;
    if (!itemId) {
      message.error('ID do item de inspeção não encontrado');
      return;
    }

    setShowForwardAutomaticModal(true);
  };

  // Detecta se o laudo mais recente foi transmitido sem cálculos
  const latestReportHasNoCalculations = (): boolean => {
    if (reports.length === 0) return false;
    const latestReport = pickEditableReport(reports);
    if (!latestReport) return false;
    const wizardData = parseReportWizardData(latestReport);
    return wizardData?.amostras?.skipCalculations === true;
  };

  const handleOpenCalculations = async (report: InspectionReport) => {
    if (!report.reportData) {
      message.error('Este laudo não possui dados para cálculos');
      return;
    }

    const wizardData = parseReportWizardData(report);
    if (!wizardData) {
      message.error('Dados do wizard não encontrados');
      return;
    }

    setCalculationsReport({ report, data: wizardData });
    setShowWizard(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number) => {
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  const canReviewReport = (): boolean => {
  if (reports.length === 0) return false;
  if (isInspector) return false;

  if (isProvider && isAwaitingProviderReview) return true;
  if (isBank && isAwaitingBankReview) return true;

  return false;
};


  if (showWizard) {
    const wizardInitialData = calculationsReport?.data || correctingReport?.data || editingReport?.data;
    const wizardEditingReportId = calculationsReport?.report.id || correctingReport?.report.id || editingReport?.report.id;

    const shouldUseChecklistOnlyMode = isInspector && (
      (hasCompletedVideoCall &&
        itemStatus === InspectionItemStatus.TelevistoriaFinalizada &&
        !correctingReport &&
        !editingReport &&
        !calculationsReport) ||
      isDevolvidoPelaPrestadora
    );

    const wizardMode = forceManualAutomatic || editingReport?.isAutomatic
      ? 'editAutomatic'
      : calculationsReport
      ? 'calculations'
      : correctingReport
      ? 'correction'
      : 'edit';

    const wizardIsAutomatic = !!editingReport?.isAutomatic
      || !!inspection.items?.[0]?.isAutomaticReport;

    const wizardReportCreatedBy = editingReport?.report.createdBy
      || correctingReport?.report.createdBy
      || calculationsReport?.report.createdBy;

    // Edição de laudo já aceito pelo banco (status 18): apenas o banco pode editar
    // após o aceite. Ao transmitir, o PDF sobe na hora e o item permanece aceito
    // (não há nova etapa de aprovação).
    const isPostApprovalEdit = isApprovedByBank && isBank && !!editingReport;

    return (
      <div>
        <LaudoWizard
          inspection={inspection}
          hasCompletedVideoCall={hasCompletedVideoCall}
          {...(wizardInitialData && { initialData: wizardInitialData })}
          {...(wizardEditingReportId && { editingReportId: wizardEditingReportId })}
          mode={wizardMode}
          isAutomaticReport={wizardIsAutomatic}
          postApprovalEdit={isPostApprovalEdit}
          {...(wizardReportCreatedBy && { reportCreatedBy: wizardReportCreatedBy })}
          {...(correctingReport && {
            reviewContext: {
              reportId: correctingReport.report.id,
              reviewerType: (correctingReport.review?.reviewerType || 'Bank') as 'Provider' | 'Bank',
              pendingCorrections: correctingReport.review?.fieldCorrections || [],
              externalReturn: correctingReport.externalReturnData,
            },
          })}
          checklistOnlyMode={shouldUseChecklistOnlyMode}
          onClose={handleCloseWizard}
        />
      </div>
    );
  }

  
  if (isLoadingVideoCheck) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px 0',
        }}
      >
        <Spin size="large" tip="Verificando chamadas de vídeo..." />
      </div>
    );
  }

  
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px 0',
        }}
      >
        <Spin size="large" tip="Carregando laudos..." />
      </div>
    );
  }

  if (isInAutomaticFlow && itemId && itemStatus !== undefined) {
    const isFailedAutomatic =
      itemStatus === InspectionItemStatus.ScrapingFalhou ||
      itemStatus === InspectionItemStatus.AnaliseIAFalhou;

    const isProcessingAutomatic =
      itemStatus === InspectionItemStatus.ProcessandoScraping ||
      itemStatus === InspectionItemStatus.AguardandoAnaliseIA ||
      itemStatus === InspectionItemStatus.ProcessandoAnaliseIA;

    const handleCancelAutomatic = async () => {
      if (!itemId) return;
      try {
        await aiAnalysisService.cancelAutomaticProcessing(itemId);
        message.success('Processamento automático cancelado. Você pode preencher o laudo manualmente.');
        setForceManualAutomatic(true);
        setShowWizard(true);
      } catch (err: any) {
        const detail = err?.response?.data?.error || err?.message || 'Erro ao cancelar processamento';
        message.error(detail);
        throw err;
      }
    };

    return (
      <div className="space-y-6">
        <AutomaticLaudoSection
          inspection={inspection}
          itemId={itemId}
          itemStatus={itemStatus}
          onReportReady={handleAutomaticReportReady}
          onRetry={handleAutomaticReportRetry}
          onPdfGenerated={() => {
            loadReports();
            onRefresh?.();
          }}
          onRefresh={onRefresh}
          {...(isBank && isFailedAutomatic && {
            onForceManual: () => {
              setForceManualAutomatic(true);
              setShowWizard(true);
            },
          })}
          {...(isBank && isProcessingAutomatic && {
            onCancelAutomatic: handleCancelAutomatic,
          })}
        />
      </div>
    );
  }

  const statusMessage = getStatusMessage();

  return (
    <div className="space-y-6">
      <LaudoTabHeader isAutomaticAiReport={false} />

      {/* Alerta de status (ex: laudo devolvido pelo sistema externo) */}
      {/* Não mostra para prestadora quando há externalReturnData (o Card já mostra tudo) */}
      {statusMessage && !(isProvider && externalReturnData) && (
        <Alert
          type={statusMessage.type}
          message={statusMessage.message}
          description={statusMessage.description}
          showIcon
          action={
            isApprovedByBank && isBank ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => {
                    const latestReport = pickEditableReport(reports);
                    if (latestReport) {
                      handleEditReport(latestReport);
                    } else {
                      message.warning('Nenhum laudo encontrado para editar');
                    }
                  }}
                  disabled={reports.length === 0}
                >
                  Editar Laudo
                </Button>
                {!inspection.items?.[0]?.isAutomaticReport && (
                  <Button
                    danger
                    icon={<SendOutlined />}
                    onClick={() => setShowReturnAfterApprovalModal(true)}
                  >
                    Devolver para Prestadora
                  </Button>
                )}
              </div>
            ) : isReturnedByExternalSystem && isBank ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  type="primary"
                  icon={<ToolOutlined />}
                  onClick={() => {
                    const latestReport = pickEditableReport(reports);
                    if (latestReport) {
                      setSelectedReport(latestReport);
                      handleCorrectReport(latestReport);
                    } else {
                      message.warning('Nenhum laudo encontrado para corrigir');
                    }
                  }}
                  disabled={reports.length === 0}
                >
                  Corrigir Diretamente
                </Button>
                <Button
                  icon={<SendOutlined />}
                  onClick={handleForwardToProvider}
                >
                  Encaminhar para Prestadora
                </Button>
              </div>
            ) : undefined
            /* Status 16 (LaudoNegadoPorBanco): o banco NÃO encaminha. A prestadora que
               fez a avaliação corrige em-lugar (ProviderCorrectionCard) e reenvia ao banco.
               O botão antigo chamava forwardAutomaticToProvider, que recriava o
               direcionamento e fazia a inspeção "renascer" (reaceite/redirecionar/reagendar). */
          }
        />
      )}

      {/* Alerta de correções pendentes */}
      {pendingReview &&
        reports.length > 0 &&
        !statusMessage &&
        !(isInspector && isRejectedByBank) && (
          <PendingCorrectionAlert pendingReview={pendingReview} />
        )}

      {/* Card de devolução ao inspetor (pré-banco) - visível para prestadora */}
      {isProvider && isAwaitingProviderReview && activeAssignment && (
        <Card style={{ marginBottom: 16, borderColor: '#faad14', background: '#fffbe6' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: 0, marginBottom: 4, color: '#d48806' }}>
                Checklist recebido do inspetor
              </h4>
              <p style={{ margin: 0, color: '#666', fontSize: 13 }}>
                {externalReturnData
                  ? 'Analise o laudo. Ajuste qualquer parte que precisar ou devolva ao inspetor caso o checklist precise de correção.'
                  : 'Analise o checklist. Se houver problemas, devolva ao inspetor para correção.'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {externalReturnData && (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => {
                    const latestReport = pickEditableReport(reports);
                    if (latestReport) {
                      handleEditReport(latestReport);
                    }
                  }}
                >
                  Editar Laudo
                </Button>
              )}
              <Button
                icon={<RollbackOutlined />}
                onClick={() => setShowReturnToInspectorModal(true)}
              >
                Devolver ao Inspetor
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Card de devolução para inspetor */}
      {isInspector && isDevolvidoPelaPrestadora && (
        <Card style={{ marginBottom: 16, borderColor: '#fa8c16' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: 0, marginBottom: 8, color: '#d48806' }}>
                Inspeção devolvida pela Prestadora para correção
              </h4>
              {activeAssignment?.notes && (
                <div style={{ marginBottom: 8 }}>
                  <strong style={{ color: '#666', fontSize: 13 }}>Observações da Prestadora:</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                    {activeAssignment.notes.split('\n').pop()}
                  </p>
                </div>
              )}
              {pendingReview?.generalComments && (
                <div style={{ marginBottom: 8 }}>
                  <strong style={{ color: '#666', fontSize: 13 }}>Comentários do Banco:</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                    {pendingReview.generalComments}
                  </p>
                </div>
              )}
              {pendingReview?.fieldCorrections && pendingReview.fieldCorrections.length > 0 && (
                <div>
                  <strong style={{ color: '#666', fontSize: 13 }}>Itens a corrigir:</strong>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: 20, color: '#666', fontSize: 13 }}>
                    {pendingReview.fieldCorrections.map((correction, index) => (
                      <li key={index} style={{ marginBottom: 4 }}>
                        <strong>{correction.fieldLabel}</strong>
                        {correction.stepName && <span style={{ color: '#999' }}> ({correction.stepName})</span>}
                        : {correction.errorMessage}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <Button
              buttonColor="primary"
              icon={<ToolOutlined />}
              onClick={() => setShowWizard(true)}
              style={{ marginLeft: 16 }}
            >
              Corrigir Checklist
            </Button>
          </div>
        </Card>
      )}

      {/* Card de correção para prestadora */}
      {isProvider && providerCanCorrect && (pendingReview || externalReturnData || isDevolvidoAposAceite) && reports.length > 0 && (
        <ProviderCorrectionCard
          pendingReview={pendingReview}
          externalReturnData={externalReturnData}
          isDevolvidoAposAceite={isDevolvidoAposAceite}
          onCorrectClick={() => {
            const latestReport = pickEditableReport(reports);
            if (latestReport) {
              setSelectedReport(latestReport);
              handleProviderCorrectMyself(latestReport);
            }
          }}
          onRedirectToInspector={activeAssignment ? () => handleRedirectToInspector() : undefined}
        />
      )}

      {/* Devolução ao inspetor agora é via Popconfirm no ProviderCorrectionCard */}

      {/* Card de ações de cálculos para o banco */}
      {isBank && isAwaitingBankReview && latestReportHasNoCalculations() && reports.length > 0 && (
        <Card
          style={{ border: '1px solid #d3adf7', background: '#f9f0ff' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CalculatorOutlined style={{ fontSize: 24, color: '#722ed1' }} />
            <div style={{ flex: 1 }}>
              <strong style={{ color: '#531dab', fontSize: 14 }}>
                Laudo sem cálculos
              </strong>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#722ed1' }}>
                Este laudo foi transmitido sem cálculos. Você pode realizar os cálculos ou encaminhar para outra prestadora.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <Button
                type="primary"
                icon={<CalculatorOutlined />}
                onClick={() => {
                  const latestReport = pickEditableReport(reports);
                  if (latestReport) {
                    handleOpenCalculations(latestReport);
                  }
                }}
              >
                Realizar Cálculos
              </Button>
              <Button
                icon={<SendOutlined />}
                onClick={() => setShowAssignCalculationsModal(true)}
              >
                Encaminhar para Cálculos
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Card de ações para prestadora de cálculos (status 29) */}
      {isProvider && isAwaitingCalculations && reports.length > 0 && (
        <Card
          style={{ border: '1px solid #d3adf7', background: '#f9f0ff' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CalculatorOutlined style={{ fontSize: 24, color: '#722ed1' }} />
            <div style={{ flex: 1 }}>
              <strong style={{ color: '#531dab', fontSize: 14 }}>
                Cálculos pendentes
              </strong>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#722ed1' }}>
                O banco encaminhou os cálculos deste laudo para você. Preencha a metodologia e transmita.
              </p>
            </div>
            <Button
              type="primary"
              icon={<CalculatorOutlined />}
              onClick={() => {
                const latestReport = pickEditableReport(reports);
                if (latestReport) {
                  handleOpenCalculations(latestReport);
                }
              }}
            >
              Abrir Cálculos
            </Button>
          </div>
        </Card>
      )}

      {/* Card para prestadora completar laudo automático */}
      {isProvider && isAutomaticReportFlow && reports.length > 0 && (
        <Card style={{ border: '1px solid #91d5ff', background: '#e6f7ff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <FileTextOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <div style={{ flex: 1 }}>
              <strong style={{ color: '#096dd9', fontSize: 14 }}>
                Laudo automático encaminhado pelo banco
              </strong>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#1890ff' }}>
                Revise os dados do laudo automático, complete as informações faltantes e transmita para o banco.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                type="primary"
                icon={<ToolOutlined />}
                onClick={() => {
                  const latestReport = pickEditableReport(reports);
                  if (latestReport) {
                    handleEditReport(latestReport);
                  }
                }}
              >
                Completar Laudo
              </Button>
              {activeAssignment && !inspection.items?.[0]?.isAutomaticReport && (
                <Button
                  icon={<SendOutlined />}
                  onClick={() => setShowAssignInspectorModal(true)}
                >
                  Direcionar para Inspetor
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Lista de laudos ou estado vazio */}
      {reports.length === 0 ? (
        <EmptyReportState
          itemStatus={itemStatus}
          hasCompletedVideoCall={hasCompletedVideoCall}
          canCreateLaudo={canCreateLaudo}
          isInspector={isInspector}
          isProvider={isProvider}
          isBank={isBank}
          isAutomaticReportFlow={isAutomaticReportFlow}
          isAwaitingBankFill={isAwaitingBankFill}
          isAwaitingProviderReview={isAwaitingProviderReview}
          onCreateLaudo={() => setShowWizard(true)}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {(() => {
            const latestReport = pickEditableReport(reports);
            if (!latestReport) return null;

            return (
              <ReportCard
                key={latestReport.id}
                report={latestReport}
                isInspector={isInspector}
                isProvider={isProvider}
                isBank={isBank}
                pendingReview={pendingReview}
                inspectorCanCreateOrEdit={inspectorCanCreateOrEdit}
                isRejectedByBank={isRejectedByBank}
                canReview={canReviewReport()}
                onView={() => handleViewReport(latestReport)}
                onReview={() => handleReviewReport(latestReport)}
                onCorrect={() => handleCorrectReport(latestReport)}
                showEditButton={isProvider && isAwaitingProviderReview && reviewHistory.length > 0 && reviewHistory[0]?.reviewerType === 'Bank' && reviewHistory[0]?.status === 'Rejected'}
                onEdit={() => handleEditReport(latestReport)}
              />
            );
          })()}
        </div>
      )}

      {/* Modais */}
      <ViewReportModal
        open={showViewModal}
        report={selectedReport}
        inspection={inspection}
        onClose={handleCloseViewModal}
      />

      <ReviewReportModal
        open={showReviewModal}
        report={selectedReport}
        reviewerType={reviewerType}
        inspection={inspection}
        hasPreviousExternalReturn={!!externalReturnData}
        onClose={handleCloseReviewModal}
        onReviewed={handleReviewed}
      />

      {itemId && (
        <AssignCalculationsModal
          open={showAssignCalculationsModal}
          onClose={() => setShowAssignCalculationsModal(false)}
          onSuccess={() => {
            setShowAssignCalculationsModal(false);
            setLocalStatusOverride(InspectionItemStatus.AguardandoCalculosPrestadora);
            loadReports();
            onRefresh?.();
          }}
          itemId={itemId}
          proposalNumber={inspection.proposalNumber}
        />
      )}

      {itemId && (
        <AssignCalculationsModal
          open={showForwardAutomaticModal}
          onClose={() => setShowForwardAutomaticModal(false)}
          onSuccess={() => {
            setShowForwardAutomaticModal(false);
            setLocalStatusOverride(InspectionItemStatus.AguardandoAceitePrestadora);
            loadReports();
            onRefresh?.();
          }}
          itemId={itemId}
          proposalNumber={inspection.proposalNumber}
          title={`ENCAMINHAR LAUDO AUTOMÁTICO - #${inspection.proposalNumber}`}
          description="O laudo automático foi devolvido pelo sistema externo. Selecione a prestadora que receberá este laudo para correção."
          successMessage="Laudo automático encaminhado para a prestadora com sucesso!"
          serviceMethod="forwardAutomaticToProvider"
        />
      )}

      {itemId && (
        <ReturnAfterApprovalModal
          open={showReturnAfterApprovalModal}
          inspectionItemId={itemId}
          onClose={() => setShowReturnAfterApprovalModal(false)}
          onSuccess={(newStatus) => {
            setShowReturnAfterApprovalModal(false);
            setLocalStatusOverride(newStatus);
            loadReports();
            onRefresh?.();
          }}
        />
      )}

      {activeAssignment && (
        <AssignInspectorModal
          open={showAssignInspectorModal}
          onClose={() => setShowAssignInspectorModal(false)}
          assignmentId={activeAssignment.id}
          providerId={activeAssignment.providerId}
          onSuccess={() => {
            setShowAssignInspectorModal(false);
            onRefresh?.();
          }}
        />
      )}
      {/* Modal de devolução ao inspetor com observações */}
      <Modal
        title="Devolver Checklist ao Inspetor"
        open={showReturnToInspectorModal}
        onOk={async () => {
          if (!returnToInspectorNotes.trim()) {
            message.warning('Informe as observações sobre o que precisa ser corrigido.');
            return;
          }
          await handleRedirectToInspector(returnToInspectorNotes.trim());
          setShowReturnToInspectorModal(false);
          setReturnToInspectorNotes('');
        }}
        onCancel={() => {
          setShowReturnToInspectorModal(false);
          setReturnToInspectorNotes('');
        }}
        okText="Devolver ao Inspetor"
        cancelText="Cancelar"
      >
        <p style={{ marginBottom: 8 }}>
          Descreva o que precisa ser corrigido no checklist para que o inspetor possa ajustar:
        </p>
        <Input.TextArea
          rows={4}
          value={returnToInspectorNotes}
          onChange={(e) => setReturnToInspectorNotes(e.target.value)}
          placeholder="Ex: Fotos da fachada estão com baixa qualidade, campo de metragem está incorreto..."
        />
      </Modal>
    </div>
  );
}
